// VerticalTabs.tsx
import React, { useEffect } from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import { useTranslation } from "react-i18next";
import { themes } from "theme/themes";
import { useSelector } from "react-redux";
import { brandingInfo } from "features/server/brandingSlice";

interface TabPanelProps {
  children?: React.ReactNode;
  value: number | false;
  index: number;
}

function TabPanel({ children, value, index, ...other }: TabPanelProps) {
  const active = value !== false && value === index;
  return (
    <div
      role="tabpanel"
      hidden={!active}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      {...other}
    >
      {active && <Box sx={{ p: 1 }}>{children}</Box>}
    </div>
  );
}

interface TabItem {
  id: number;
  title: string;
  child: React.ReactNode; // React component as child
}

interface VerticalTabsProps {
  tabs: TabItem[];
  /** Use -1 so no tab is selected (e.g. hidden tab route still open). */
  selectedIndex: number;
  handleChangeTabs?: (tabId: number) => void;
  disableTabNo?: number[];
}

const VerticalTabs: React.FC<VerticalTabsProps> = ({
  tabs,
  selectedIndex,
  handleChangeTabs,
  disableTabNo,
}) => {
  const [value, setValue] = React.useState<number | false>(
    selectedIndex < 0 ? false : selectedIndex,
  );
  const { t } = useTranslation();
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    handleChangeTabs?.(newValue);
  };
  useEffect(() => {
    setValue(selectedIndex < 0 ? false : selectedIndex);
  }, [selectedIndex]);
  return (
    <Box
      className="flex "
      sx={{ "& .MuiTab-root": { textTransform: "capitalize" } }}
    >
      <div
        className="rounded-lg w-[200px] min-w-[200px] h-[fit-content] pt-[7px] pb-[10rem] sticky top-[80px]"
        style={{ background: themeColors?.primary }}
      >
        {/* <div
          className='min-h-[93px] flex items-center justify-center text-white border-b border-b-white'
        >
          <h1 className='font-Saira font-[500] text-[15px]'> 
            {tabs[value].title}
          </h1>
        </div> */}
        <Tabs
          orientation="vertical"
          variant="scrollable"
          value={value}
          onChange={handleChange}
          aria-label="Vertical tabs example"
          // sx={{
          //   '& .MuiTabs-indicator': {
          //     backgroundColor: 'orange',
          //   },
          // }}
          className="border-r border-[#FFFFFF] w-[100%] py-0 px-0 text-white"
        >
          {tabs.map((tab) => (
            <Tab
              key={tab.id}
              disabled={disableTabNo?.includes(tab.id)}
              label={t(tab.title)}
              className={`font-Saira font-[500] text-[11px] border-r-2 border-solid text-white border-b border-b-white`}
              sx={{
                background:
                  value === tab.id
                    ? themeColors?.secondary
                    : disableTabNo?.includes(tab.id)
                      ? "#B8C0C6"
                      : themeColors?.primary,
                borderColor: themeColors?.secondary,
                "&:hover": { background: themeColors?.secondary },
              }}
            />
          ))}
        </Tabs>
      </div>
      <Box className="grow">
        {tabs.map((tab) => (
          <TabPanel key={tab.id} value={value} index={tab.id}>
            <Box className="w-[100%]">{tab.child}</Box>
          </TabPanel>
        ))}
      </Box>
    </Box>
  );
};

export default VerticalTabs;
